/**
 * Кеш ответов API на время жизни вкладки — основа stale-while-revalidate (см. useApi).
 *
 * Смысл: уже виденные данные показываются мгновенно, а свежие подтягиваются фоном.
 * Переход «Новости → Контакты → Новости» больше не рисует спиннер и не ждёт сеть:
 * страница появляется сразу с прошлым ответом, и если сервер прислал что-то новое,
 * содержимое молча обновляется.
 *
 * Здесь НЕТ импорта api-клиента, и это намеренно: клиент, наоборот, импортирует отсюда
 * clearCache (сессия умерла — кеш чужой). Обратный импорт замкнул бы круг.
 *
 * Модуль хранит три вещи на каждый ключ (ключ = путь запроса):
 *   значение          — последний успешный ответ;
 *   когда получено     — чтобы не перезапрашивать одно и то же по десять раз подряд;
 *   запрос в полёте    — чтобы два компонента на одной странице не дёргали сервер дважды.
 */

type Listener = () => void

const values = new Map<string, unknown>()
const fetchedAt = new Map<string, number>()
const inFlight = new Map<string, Promise<unknown>>()
const listeners = new Map<string, Set<Listener>>()

/** Данные свежее этого срока повторно не запрашиваются (защита от шквала перерисовок). */
const FRESH_MS = 2000

export function getCached<T>(key: string): T | undefined {
  return values.get(key) as T | undefined
}

export function isFresh(key: string): boolean {
  const at = fetchedAt.get(key)
  return at !== undefined && Date.now() - at < FRESH_MS
}

/** Положить значение и разбудить всех, кто на него подписан (в т.ч. на других страницах). */
export function setCached(key: string, value: unknown): void {
  values.set(key, value)
  fetchedAt.set(key, Date.now())
  listeners.get(key)?.forEach((notify) => notify())
}

/**
 * Данные устарели после нашей же правки (создали новость, оплатили счёт).
 * Значение НЕ стираем: пусть на экране остаётся старое, пока грузится новое —
 * иначе таблица моргнёт пустотой. Стираем только отметку свежести.
 */
export function invalidate(key: string): void {
  fetchedAt.delete(key)
  listeners.get(key)?.forEach((notify) => notify())
}

/** Сессия сменилась или закончилась — весь кеш чужой. */
export function clearCache(): void {
  values.clear()
  fetchedAt.clear()
  inFlight.clear()
  listeners.forEach((set) => set.forEach((notify) => notify()))
}

export function subscribe(key: string, listener: Listener): () => void {
  const set = listeners.get(key) ?? new Set<Listener>()
  set.add(listener)
  listeners.set(key, set)

  return () => {
    set.delete(listener)
    if (set.size === 0) listeners.delete(key)
  }
}

/**
 * Загрузка с дедупликацией: если этот же ключ уже грузится, второй вызов не шлёт
 * новый запрос, а ждёт первый. Так карточка участка и её счета, оказавшись на одной
 * странице с одним и тем же запросом, дёргают сервер один раз, а не дважды.
 */
export async function fetchShared<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const running = inFlight.get(key)
  if (running !== undefined) return running as Promise<T>

  const attempt = loader()
    .then((result) => {
      setCached(key, result)
      return result
    })
    .finally(() => {
      inFlight.delete(key)
    })

  inFlight.set(key, attempt)
  return attempt
}
